import { alpha, createTheme } from '@mui/material/styles';
declare module '@mui/material/styles' {
  interface Palette {
    piechart: {
      netBanking: string;
      debitCard: string;
      enachmandates?: string;
      upimandates?: string;
      upi: string;
    };
    areaChart: {
      debitcard: string;
      netBanking: string;
      enachmandates: string;
      upimandates: string;
      upi: string;
      totalTransactions: string;
      successTranscations: string;
      failedTranscations: string;
    };
  }

  interface Theme {
    borders: {
      borderLight: string;
      borderMedium: string;
      borderTime: string;
      borderReport: string;
      borderForm: string;
    };
  }

  interface PaletteOptions {
    piechart?: {
      netBanking?: string;
      debitCard?: string;
      enachmandates?: string;
      upimandates?: string;
      upi?: string;
    };
    areaChart?: {
      debitcard?: string;
      netBanking?: string;
      mandates?: string;
      upi?: string;
      totalTransactions?: string;
      successTranscations?: string;
      failedTranscations?: string;
    };
  }
  interface ThemeOptions {
    borders?: {
      borderLight?: string;
      borderMedium?: string;
      borderTime?: string;
      borderReport?: string;
      borderForm?: string;
    };
    customColors?: {
      warm?: string;
      light?: string;
      fade?: string;
      green?: string;
      red?: string;
    };
  }
}
const defaultTheme = createTheme();
defaultTheme.shadows.pop();
defaultTheme.shadows.pop();
defaultTheme.shadows.push('0px 2px 2px rgba(0, 0, 0, 0.2)');
defaultTheme.shadows.push('0px 4px 8px rgba(0, 0, 0, 0.1)');
defaultTheme.shadows.push('0px 8px 16px rgba(0, 0, 0, 0.1)');
// Step 1: Create the theme object without using `theme.breakpoints`
let theme = createTheme({
  shadows: [...defaultTheme.shadows],
  palette: {
    primary: {
      main: '#2A6AE1',
      light: '#2A6AE116',
      dark: '#2A6AE18F',
    },
    secondary: {
      main: '#5251521F',
      light: '#525152CC',
    },
    success: {
      main: '#0ECB81', // Customize the success color
      light: '#0ECB811F',
      dark: '#34C759',
    },
    error: {
      main: '#F14366', // Customize the error color
      light: '#F143661F',
    },
    warning: {
      main: '#F79009',
      light: '#F790091F',
    },
    background: {
      default: '#F5F6F8',
    },
    common: {
      black: '#000000',
      white: '#FFFFFF',
    },
    divider: '#CDCDCD',

    piechart: {
      netBanking: '#8342F6',
      debitCard: '#2CAFFE',
      enachmandates: '#FE6A35',
      upimandates: '#DD3D3D',
      upi: '#F4AE3D',
    },
    areaChart: {
      debitcard: '#8342F6',
      netBanking: '#2A6AE1',
      mandates: '#51B6D5',
      upi: '#F4AE3D',
      totalTransactions: '#2A6AE1',
      successTranscations: '#0ECB81',
      failedTranscations: '#F1436680',
    },
  },
  shape: {
    borderRadius: 5, // Set a default border radius
  },
  borders: {
    borderLight: '0.5px solid #CDCDCD',
    borderMedium: '1px solid #2A6AE1',
    borderTime: '0.5px solid #5251528F',
    borderReport: '1px solid #e0e0e0',
    borderForm: `0.7px solid ${alpha('#6C737F', 0.5)}`,
  },
  typography: {
    fontFamily: 'Montserrat',
    h1: {
      fontSize: '24px',
      fontWeight: 600,
      color: '#020202',
      lineHeight: '29.26px',
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontSize: '18px',
      fontWeight: 600,
      color: '#020202',
      lineHeight: '21.94px',
    },
    h4: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#020202',
      lineHeight: '19.53px',
    },
    subtitle1: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#525152',
      lineHeight: '17.07px',
    },
    subtitle2: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#525152',
      lineHeight: '14.63px',
    },
    body1: {
      fontSIze: '12px',
    },
  },

  customColors: {
    warm: '#F7900940', // Custom color for pending status
    light: '#FFF3E0', // Another custom color
    fade: '#F1436640',
    green: '#087047',
    red: '#F14366',
  },
});

// Step 2: Extend the theme to include `theme.breakpoints`
theme = createTheme(theme, {
  customColors: {
    warm: '#F7900940', // Add custom background color
    light: '#F79009',
    green: '#087047',
    red: '#F14366',
  },
  dangerColor: {
    danger: '#F1436640',
    light: '#F14366',
  },
  components: {
    MuiSelect: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: '16px',
          color: '#2A6AE1',
          '& .MuiSelect-select': {
            border: 'none',
            p: '3px',
          },
          '&:before': {
            borderBottom: '3px dotted #2A6AE1',
          },
          '&:hover:not(.Mui-disabled):before': {
            borderBottom: '2px dotted #2A6AE1',
          },
          '&:after': {
            borderBottom: '2px dotted #2A6AE1',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: 'Montserrat, sans-serif',
          color: '#6C737F',
          fontWeight: 500,
          backgroundColor: 'white',
          [theme.breakpoints.up('sm')]: {
            m: '3px', // Apply margin for screen sizes 'sm' and above
          },
          [theme.breakpoints.down('sm')]: {
            m: '0', // Apply margin for screen sizes below 'sm'
          },
          '&:hover': {
            backgroundColor: '#2A6AE18F',
            color: '#2A6AE1',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 500,
          },
          '&.Mui-selected:hover': {
            backgroundColor: '#2A6AE18F',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '7px',
          border: 'none', // Remove border for all buttons
          '&:hover': {
            backgroundColor: '#f0f0f0',
            border: 'none',
          },
        },
        contained: {
          backgroundColor: '#2A6AE1',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#2A6AE18F',
          },
        },
        outlined: {
          color: '#2A6AE1',
          borderColor: 'none',
          '&:hover': {
            backgroundColor: '#f0f0f0',
            borderColor: 'none',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'black', // Border color on hover
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'black', // Border color when focused
            borderWidth: '1px', // Border width when focused
          },
          '& .MuiInputAdornment-root': {
            '& .MuiSvgIcon-root': {
              color: 'black',
              opacity: 0.5, // Icon color and opacity
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          height: '55px', // Set height globally for TextField
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: theme.typography.body1.fontSize, // Set the font size for labels
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          backgroundColor: '#E6F4EA', // stronger green
          color: '#1E4620',
        },
        standardError: {
          backgroundColor: '#FDECEA',
          color: '#611A15',
        },
        standardWarning: {
          backgroundColor: '#FFF4E5',
          color: '#663C00',
        },
        standardInfo: {
          backgroundColor: '#E8F4FD',
          color: '#0D3C61',
        },
      },
    },
    // MuiInputBase: {
    //   styleOverrides: {
    //     input: {
    //       padding: '12px !important',
    //     },
    //   },
    // },
  },
});

export default theme;
